import React from "react"
import { AlignLeftIcon, PercentIcon } from "lucide-react"

import { UserData } from "@/lib/intershop/interfaces/user/user"
import { getCountry, getIsoLocaleFromSbLocale } from "@/lib/locale/locale-common-utils"
import { Category } from "@/lib/storefront/category/category"
import { cn } from "@/lib/utils"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/components"
import { UbHeaderStoryblok } from "@/components/storyblok/component-types"
import StoryblokPrivateCustomerToggle from "@/components/storyblok/header/StoryblokPrivateCustomerToggle"

import StoryblokMultilink from "../common/StoryblokMultilink"
import { UbHeaderLinkStoryblokExtended } from "../UBHeaderClient"
import StoryblokCategories from "./StoryblokCategories"
import StoryblokMobileCategories from "./StoryblokMobileCategories"

interface StoryblokMenuBarProps {
  blok: UbHeaderStoryblok
  isMobileVisible: boolean
  categories: Category[]
  navigation: UbHeaderLinkStoryblokExtended[]
  toolNavigation?: UbHeaderLinkStoryblokExtended[]
  userData: UserData | undefined
  isB2BCustomer: boolean
  displayIsB2BCustomer?: boolean
  sbLocale: string
  oci: boolean
  labelAllCategories: string
  categoriesLoading?: boolean
  variant?: "default" | "rebrush"
}

function HeaderRebrushIcon({ name, className }: { name: "menu-02" | "chevron-down"; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block shrink-0 bg-current [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]",
        className
      )}
      style={{
        maskImage: `url(/img/icons/header-rebrush/${name}.svg)`,
        WebkitMaskImage: `url(/img/icons/header-rebrush/${name}.svg)`,
      }}
      aria-hidden="true"
    />
  )
}

export default function StoryblokMenuBar({
  blok,
  isMobileVisible,
  categories,
  navigation,
  toolNavigation = [],
  userData,
  isB2BCustomer,
  displayIsB2BCustomer,
  sbLocale,
  oci,
  labelAllCategories,
  categoriesLoading,
  variant = "default",
}: StoryblokMenuBarProps) {
  const country = getCountry(getIsoLocaleFromSbLocale(sbLocale))
  const ubHeaderLinkFilter = (ubHeaderLinkItem: UbHeaderLinkStoryblokExtended) =>
    !(
      ubHeaderLinkItem.countries_exclude?.includes(country) ||
      (ubHeaderLinkItem.hideOCI && oci) ||
      (!oci && ((ubHeaderLinkItem.hideB2C && !isB2BCustomer) || (ubHeaderLinkItem.hideB2B && isB2BCustomer)))
    )

  const visibleNavigation = navigation.filter(ubHeaderLinkFilter)
  const visibleToolNavigation = toolNavigation.filter(ubHeaderLinkFilter)

  if (variant === "rebrush") {
    const navItemClassName = `
      flex min-h-[34px] items-center gap-1.5 rounded-[4px] px-1.5 py-1.5 text-base leading-[18px] font-normal whitespace-nowrap text-[#303030]
      hover:bg-[#F8F8F8] focus-visible:ring-2 focus-visible:ring-ub-hlblue focus-visible:outline-hidden
    `

    return (
      <div className="w-full bg-white shadow-[inset_0_-1px_0_#EEEEEE] lg:shadow-[inset_0_1px_0_#EEEEEE,inset_0_-1px_0_#EEEEEE]">
        {isMobileVisible ? (
          <div className="flex size-full flex-col gap-0 bg-white lg:hidden lg:gap-0.5">
            <StoryblokMobileCategories blok={blok} categories={categories} sbLocale={sbLocale} />
            {[...visibleNavigation, ...visibleToolNavigation].map((ubHeaderLinkItem) => (
              <StoryblokMultilink
                link={ubHeaderLinkItem.multilink}
                sbLocale={sbLocale}
                key={ubHeaderLinkItem.label}
                className={cn("px-5 py-2 text-lg font-semibold text-[#303030] hover:bg-[#F8F8F8]", {
                  "text-primary": ubHeaderLinkItem.highlight,
                })}
              >
                {ubHeaderLinkItem.label}
              </StoryblokMultilink>
            ))}
          </div>
        ) : (
          <div className="mx-auto hidden w-full max-w-7xl px-4 py-1.5 sm:px-6 lg:flex lg:px-8">
            <div className="flex w-full items-center justify-start gap-6 text-[#303030]">
              <NavigationMenu className="bg-none">
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      className="
                        rounded-[4px] bg-transparent px-1.5 py-1.5 text-[#303030]
                        hover:bg-[#F8F8F8] hover:text-[#303030]
                        focus:bg-transparent focus:text-[#303030] focus-visible:ring-2 focus-visible:ring-ub-hlblue
                        focus-visible:outline-hidden
                        data-active:bg-[#F8F8F8] data-active:text-[#303030]
                        data-open:bg-[#F8F8F8] data-open:text-[#303030]
                        data-[state=open]:bg-[#F8F8F8] data-[state=open]:text-[#303030]
                        [&>svg:last-child]:hidden
                      "
                    >
                      <div className="flex min-w-36 flex-row items-center gap-1.5">
                        <HeaderRebrushIcon name="menu-02" className="size-6 text-black" />
                        <p className="text-base leading-[18px] font-semibold whitespace-nowrap">{labelAllCategories}</p>
                        <HeaderRebrushIcon name="chevron-down" className="size-4 text-[#303030]" />
                      </div>
                    </NavigationMenuTrigger>
                    <NavigationMenuContent className={"w-full rounded-none p-12 shadow-none"}>
                      <StoryblokCategories
                        blok={blok}
                        categories={categories}
                        sbLocale={sbLocale}
                        disableTeaserContent={oci}
                      />
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              <span className="w-0.5 self-stretch bg-[#EEEEEE]" aria-hidden="true" />

              <div className="flex min-w-0 flex-1 items-center gap-6 overflow-visible">
                {visibleNavigation.map((ubHeaderLinkItem) => (
                  <StoryblokMultilink
                    link={ubHeaderLinkItem.multilink}
                    sbLocale={sbLocale}
                    key={ubHeaderLinkItem.label}
                    className={cn(navItemClassName, {
                      "text-[#303030]": ubHeaderLinkItem.highlight,
                    })}
                  >
                    {ubHeaderLinkItem.highlight && (
                      <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-primary text-xs text-white">
                        <PercentIcon className="size-3.5" strokeWidth={3} />
                      </span>
                    )}
                    {ubHeaderLinkItem.label}
                  </StoryblokMultilink>
                ))}
              </div>

              {visibleToolNavigation.length > 0 && (
                <>
                  <span className="w-0.5 self-stretch bg-[#EEEEEE]" aria-hidden="true" />
                  <div className="flex shrink-0 items-center gap-6">
                    {visibleToolNavigation.map((ubHeaderLinkItem) => (
                      <StoryblokMultilink
                        link={ubHeaderLinkItem.multilink}
                        sbLocale={sbLocale}
                        key={ubHeaderLinkItem.label}
                        className={navItemClassName}
                      >
                        {ubHeaderLinkItem.label}
                      </StoryblokMultilink>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="w-full bg-black">
      {isMobileVisible ? (
        <div className="flex size-full flex-col gap-0 bg-white lg:hidden lg:gap-0.5">
          {!oci && (
            <div className={"m-2"}>
              <StoryblokPrivateCustomerToggle
                userData={userData}
                isMobileVisible={isMobileVisible}
                isB2BCustomer={displayIsB2BCustomer}
              />
            </div>
          )}
          <StoryblokMobileCategories
            blok={blok}
            categories={categories}
            sbLocale={sbLocale}
            loading={categoriesLoading}
          />
          {visibleNavigation.map((ubHeaderLinkItem) => (
            <StoryblokMultilink
              link={ubHeaderLinkItem.multilink}
              sbLocale={sbLocale}
              key={ubHeaderLinkItem.label}
              className={cn("bg-black px-5 py-1.5 text-lg font-semibold text-white", {
                "border border-white bg-primary hover:bg-primary-hover hover:text-white lg:border-2":
                  ubHeaderLinkItem.highlight,
              })}
            >
              {ubHeaderLinkItem.label}
            </StoryblokMultilink>
          ))}
        </div>
      ) : (
        <div className="mx-auto hidden w-full max-w-7xl justify-center px-4 sm:px-6 lg:flex lg:px-8">
          <div className="flex w-full max-w-7xl items-center justify-start font-bold text-white">
            <NavigationMenu className="bg-none">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className="
                      rounded-none bg-transparent p-0 pr-12
                      hover:bg-black hover:text-white
                      focus:bg-black focus:text-white
                      data-active:bg-black data-active:text-white
                      data-open:bg-black data-open:text-white
                      data-[state=open]:bg-black data-[state=open]:text-white
                    "
                  >
                    <div className="flex min-w-36 flex-row items-center gap-2">
                      <AlignLeftIcon className={"h-[20px] text-white"} />
                      <p className="text-lg font-semibold whitespace-nowrap uppercase">{labelAllCategories}</p>
                    </div>
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className={"w-full rounded-none p-12 shadow-none"}>
                    <StoryblokCategories
                      blok={blok}
                      categories={categories}
                      sbLocale={sbLocale}
                      disableTeaserContent={oci}
                      loading={categoriesLoading}
                    />
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {visibleNavigation.map((ubHeaderLinkItem) => (
              <StoryblokMultilink
                link={ubHeaderLinkItem.multilink}
                sbLocale={sbLocale}
                key={ubHeaderLinkItem.label}
                className={cn(
                  `
                    flex h-full items-center rounded-[1.25rem] px-6 text-lg/6 font-semibold whitespace-nowrap text-white
                    hover:bg-white hover:text-black
                  `,
                  {
                    "rounded-[1.25rem] border border-white bg-primary hover:bg-primary-hover hover:text-white lg:border-2":
                      ubHeaderLinkItem.highlight,
                  }
                )}
              >
                {ubHeaderLinkItem.label}
              </StoryblokMultilink>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
